import { useStanding } from '../hooks/useInStanding'
import type { Standing } from '../hooks/useInStanding'
import { usePageTitle } from '../hooks/usePageTitle'
import {
  BrassRule,
  ClubLinkButton,
  ClubPage,
  Container,
  Crest,
  Eyebrow,
  Masthead,
  SectionHead,
} from '../components/golf'
import { formatMemberNumber, foundingPlate } from '../lib/membership'
import type { FoundingPlate, FoundingSeat } from '../lib/membership'

/**
 * The Club (route map §4 `/the-club`) — the charter, the course, and the book.
 *
 * THE FOUNDING ROLL IS REAL. The design card lists seven named members and
 * declares "eighty-seven of one hundred spoken for"; none of them exist, so the
 * roll used to ship unengraved — nine numbered lines, all "Unclaimed". It now
 * reads the founding plate the shell injects (`Club::FoundingRoll`), engraving
 * each struck seat with its number AND the member's name. The names are a
 * deliberate decision with the privacy consequence stated and accepted
 * (decision record §7, 2026-08-22 —
 * `../../../../../docs/fireside/club-web-decisions.md`); that service's header
 * is where the reasoning lives.
 *
 * ⚠️ The unclaimed lines carry NO number. Member numbers are struck server-side
 * as `max + 1` across every tier, so the next founding seat is not necessarily
 * the next integer — printing "No. 004" beside an empty line would be the club
 * guessing which number it is about to issue. An engraver's blank line is the
 * honest shape, and the number appears when the seat is actually struck. The
 * line UNDER the plate obeys the same rule (`plateNote`): it says whether the
 * plate is empty, never which number is going next.
 */

const ARTICLES = [
  {
    numeral: 'Article the First',
    text: 'That every member belongs — by name, by number, and by right of dues honestly settled.',
  },
  {
    numeral: 'Article the Second',
    text: 'That every member may slip out whenever they must, without ceremony, explanation, or goodbyes.',
  },
]

const COURSE_STATS = [
  { value: '18', label: 'Holes, always open' },
  { value: '0', label: 'Tee times required' },
  { value: 'MMXXVI', label: 'First season of many' },
]

const STANDING = [
  {
    numeral: 'I.',
    title: 'The Register',
    body: 'Every round entered, every score kept. Standings are published to the membership — and to no one else on earth.',
  },
  {
    numeral: 'II.',
    title: 'Handicaps, Honoured',
    body: 'Your number is computed with ceremony and adjusted without judgement. Improvement is noted. Decline is forgiven.',
  },
  {
    numeral: 'III.',
    title: 'Discretion, Assured',
    body: 'What happens on the grounds stays in the book. We record the score, never the excuse.',
  },
]

/**
 * The line above the door, by standing. A member is told the clubhouse is
 * through there; anyone the club knows holds nothing is invited to apply; a
 * signed-in viewer the club has NOT been told about — standing still loading,
 * or the read failed — gets no line at all, because either sentence would be
 * a claim the club cannot back (decision record §12.2).
 */
const DOOR_LEAD_IN: Record<Standing, string | null> = {
  member: 'Your standing is in good order; the clubhouse is through there.',
  none: 'Apply this evening and hold a number of your own by morning — a card, a standing, and eighteen holes that answer to your calendar.',
  unknown: null,
}

/** The plate keeps its shape: at least this many lines, engraved or not. */
const PLATE_LINES = 9

interface PlateLine {
  key: string
  number: string | null
  name: string
}

/**
 * The plate as it is engraved: struck seats first, in the order their numbers
 * were issued, then blank lines to keep the plate's shape.
 *
 * Capped at the hundred, because a plate of a hundred seats shows a hundred
 * lines and the grid wraps them; a roll longer than the charter would be a
 * server bug, and the page prints what the charter allows rather than
 * whatever arrived.
 */
function plateLines(roll: FoundingSeat[], seats: number): PlateLine[] {
  const engraved = (seats > 0 ? roll.slice(0, seats) : roll).map((seat) => ({
    key: String(seat.member_number),
    number: formatMemberNumber(seat.member_number),
    name: seat.display_name,
  }))
  const blanks = Math.max(0, PLATE_LINES - engraved.length)
  return [
    ...engraved,
    ...Array.from({ length: blanks }, (_, index) => ({
      key: `blank-${index}`,
      number: null,
      name: 'Unclaimed',
    })),
  ]
}

/**
 * The line under the plate, from the plate — or NOTHING, twice over.
 *
 * ⚠️ It promises no NUMBER. It used to close with "No. 001 is still to be
 * had", which is false the moment any member of any tier exists: numbers are
 * struck server-side as `max + 1` ACROSS EVERY TIER, so with three weekenders
 * on the books the next founding seat is No. 004. The club can say the plate is
 * empty — it knows that — and cannot say which number the next founder gets
 * until it strikes one.
 *
 * `seats === 0` is the shell having said nothing about the plate at all
 * (`foundingPlate`), which is a NO ANSWER rather than an empty plate. A page
 * with no answer prints no counter — the alternative is a hardcoded hundred,
 * which is the drift the injected payload exists to remove.
 */
function plateNote({ taken, seats }: FoundingPlate): string | null {
  if (seats === 0) return null
  if (taken === 0) {
    return `The plate is blank and the engraver is waiting. Not one of the ${seats} is spoken for.`
  }
  if (taken >= seats) {
    return `All ${seats} are struck. The plate is closed and the engraver has gone home.`
  }
  return `${taken} of ${seats} spoken for. The engraver is still at work.`
}

export function TheClubScreen() {
  usePageTitle('The Club')
  const standing = useStanding()
  const leadIn = DOOR_LEAD_IN[standing]
  const plate = foundingPlate()
  const lines = plateLines(plate.roll, plate.seats)
  const note = plateNote(plate)

  return (
    <ClubPage>
      {/* 84px over, 78px under, then the charter section's own 14px: the
          92px the card leaves between the tagline and the charter card
          (the-club.html:62,71). */}
      <section className="pb-19.5 pt-21">
        <Container>
          <Masthead
            eyebrow="The Club"
            title="A members’ club that keeps"
            emphasis="your hours."
            estd="A Private Virtual Club · Chartered MMXXVI"
            tagline="The ceremony of the fairway, kept wherever you keep yourself."
          />
        </Container>
      </section>

      {/* ── The charter ── */}
      <section className="pb-23 pt-3.5">
        <Container>
          {/* the-club.html:72 `padding:60px 68px 52px`, :142 `44px 34px 40px` on a phone. */}
          <div className="on-pine relative overflow-hidden rounded-club-lg border border-gilt/45 bg-pine px-8.5 pb-10 pt-11 text-paper sm:px-17 sm:pb-13 sm:pt-15">
            <span
              aria-hidden="true"
              className="absolute -right-22 -top-30 size-[300px] rounded-full border border-gilt/20"
            />
            <span
              aria-hidden="true"
              className="absolute -bottom-24 -left-17 size-[220px] rounded-full border border-gilt/20"
            />

            <div className="relative z-10">
              <Eyebrow tone="gilt">The Charter · An Excerpt</Eyebrow>
              <p className="mb-9 mt-4 max-w-[24ch] font-display text-[clamp(1.625rem,3.2vw,2.125rem)] leading-[1.26] text-paper">
                Be it resolved: the club keeps two promises, and keeps them for life.
              </p>

              <div className="grid gap-11 border-t border-gilt/35 pt-8 md:grid-cols-2">
                {ARTICLES.map((article) => (
                  <div key={article.numeral}>
                    <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gilt">
                      {article.numeral}
                    </h2>
                    <p className="mt-2.5 font-display text-xl leading-[1.44] text-paper/95">
                      {article.text}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-11 flex flex-wrap items-baseline justify-between gap-5">
                <span className="font-display text-lg italic text-gilt">
                  Set down by the Founding Steward
                </span>
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-sage">
                  Chartered in the year MMXXVI
                </span>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── The course ── */}
      <section className="border-y border-brass/30 bg-paper-deep py-21">
        <Container className="grid items-center gap-15 lg:grid-cols-[0.94fr_1.06fr]">
          <div>
            <Eyebrow>The Course</Eyebrow>
            <h2 className="mb-4.5 mt-3.5 font-display text-[clamp(1.875rem,3.8vw,2.625rem)] font-medium leading-[1.04] text-pine">
              Always in season. Never overbooked.
            </h2>
            <p className="mb-3.5 max-w-[46ch] text-[15.5px] text-ink-soft">
              The grounds are drawn, not mown: eighteen holes rendered in fairway green, open at
              every hour and in every weather, on whichever continent you happen to be excusing
              yourself from.
            </p>
            <p className="mb-3.5 max-w-[46ch] text-[15.5px] text-ink-soft">
              There is no tee sheet and no ranger — only a round that fits the day. The full
              eighteen on a defended Sunday, or one immaculate par three between obligations.
            </p>

            {/* One line at desktop width, as the card keeps it (the-club.html:50
                `.trust{display:flex}` never wraps): the three stats shrink and
                their labels break inside them, instead of the third stat
                dropping to a second row in the .94fr column. Below `lg` the
                column is the page and the row may wrap. */}
            <dl className="mt-11 flex flex-wrap gap-7 border-t border-brass/35 pt-6 lg:flex-nowrap">
              {COURSE_STATS.map((stat) => (
                <div key={stat.label}>
                  <dd className="font-display text-[28px] text-pine">{stat.value}</dd>
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
                    {stat.label}
                  </dt>
                </div>
              ))}
            </dl>
          </div>

          {/* The course book — a surveyor's plate, and it names NO course. The
              drawing is the club's own and stays; its caption used to read "The
              7th at Exit Links · Par 4 · 402 yds", one of two survivals of the
              fiction the Grounds retired. Both were put back to the maintainer
              and dropped by his answer (`club-web-decisions.md` §10,
              2026-08-22): the plate keeps the hand, not the invented course or
              its invented yardage. */}
          <figure className="rounded-club-lg border border-ink/12 bg-parchment px-6.5 pb-5 pt-6 shadow-[0_28px_56px_-38px_rgba(21,58,42,0.45)]">
            <figcaption className="mb-3.5 flex items-baseline justify-between text-[10.5px] font-semibold uppercase tracking-[0.2em] text-brass-deep">
              <span>The Course Book</span>
              <span>Plate VII</span>
            </figcaption>
            <svg viewBox="0 0 560 300" fill="none" aria-hidden="true" className="block h-auto w-full">
              <path d="M20 172 Q160 112 295 148 T545 126" stroke="var(--color-fairway)" strokeWidth="1.5" opacity=".8" />
              <path d="M20 208 Q160 148 295 184 T545 162" stroke="var(--color-sage)" strokeWidth="1.5" />
              <path d="M20 242 Q160 188 295 218 T545 202" stroke="var(--color-brass)" strokeWidth="1" opacity=".55" />
              <path d="M20 272 Q160 226 295 250 T545 238" stroke="var(--color-sage)" strokeWidth="1" opacity=".7" />
              <ellipse cx="468" cy="158" rx="36" ry="15" stroke="var(--color-fairway)" strokeWidth="1.5" fill="var(--color-sage-soft)" fillOpacity=".45" />
              <path d="M64 236 Q290 52 462 148" stroke="var(--color-ink)" strokeWidth="1.2" strokeDasharray="2 7" opacity=".5" />
              <circle cx="64" cy="238" r="3.5" fill="var(--color-pine)" />
              <line x1="60" y1="252" x2="68" y2="252" stroke="var(--color-brass)" strokeWidth="1.5" />
              <line x1="468" y1="158" x2="468" y2="106" stroke="var(--color-pine)" strokeWidth="1.6" />
              <path d="M468 106 L468 122 L492 114 Z" fill="var(--color-flag)" />
              <circle cx="468" cy="158" r="2.2" fill="var(--color-pine)" />
            </svg>
            <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3.5 border-t border-brass/35 pt-3.5">
              <span className="font-display text-[17px] text-pine">
                A hole drawn for the charter
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] tabular-nums text-brass-deep">
                Par 4 · Dogleg, polite
              </span>
            </div>
          </figure>
        </Container>
      </section>

      {/* ── In good standing ── */}
      <section className="pb-21 pt-22">
        <Container>
          <SectionHead centered eyebrow="In Good Standing" title="The club keeps its book." className="mb-13" />
          <div className="grid gap-8 sm:grid-cols-3">
            {STANDING.map((pillar) => (
              <div key={pillar.title}>
                <BrassRule short className="mb-4" />
                <span className="font-display text-[15px] text-brass-deep">{pillar.numeral}</span>
                <h3 className="mb-2 mt-2.5 font-display text-xl font-medium text-pine">
                  {pillar.title}
                </h3>
                <p className="text-[14.5px] text-ink-soft">{pillar.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── The founding hundred ── */}
      <section className="pb-24">
        <Container>
          <div className="relative rounded-club border border-brass bg-parchment px-8 py-11 text-center sm:px-12 sm:py-13">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-[7px] rounded-club-sm border border-brass/45"
            />
            <Crest className="mx-auto mb-4.5 block size-16" />
            <p className="text-[12.5px] font-semibold uppercase tracking-[0.34em] text-brass-deep">
              The Founding Hundred
            </p>
            <h2 className="mb-2.5 mt-3 font-display text-[clamp(1.75rem,3.4vw,2.25rem)] font-medium leading-[1.04] text-pine">
              Chartered names, kept in brass.
            </h2>
            <p className="mx-auto max-w-[52ch] text-[14.5px] text-ink-soft">
              The first one hundred members are inscribed here and in the charter — their rate held
              for life, their numbers never reissued.
            </p>

            <ul className="mx-auto mt-8 grid max-w-[780px] gap-x-11 gap-y-3 border-t border-brass/35 pt-6.5 sm:grid-cols-2 lg:grid-cols-3">
              {lines.map((line) => (
                <li key={line.key} className="flex items-baseline gap-3 text-[13.5px] tabular-nums">
                  <span className="font-display tracking-[0.06em] text-brass-deep">
                    {line.number ?? '—'}
                  </span>
                  <span aria-hidden="true" className="-translate-y-1 flex-1 border-b border-dotted border-brass/50" />
                  <span className={line.number ? 'text-pine' : 'text-ink-soft'}>{line.name}</span>
                </li>
              ))}
            </ul>

            {note && <p className="mt-7 font-display text-[15.5px] italic text-fairway">{note}</p>}
          </div>
        </Container>
      </section>

      {/* ── The door is open ── */}
      <section className="on-pine relative overflow-hidden bg-pine py-24 text-center text-paper">
        <span aria-hidden="true" className="absolute -left-20 -top-30 size-[340px] rounded-full border border-gilt/25" />
        <span aria-hidden="true" className="absolute -bottom-28 -right-10 size-[260px] rounded-full border border-gilt/25" />
        <Container>
          <Eyebrow tone="gilt">The Door Is Open</Eyebrow>
          <h2 className="mx-auto mb-5 mt-4 max-w-[18ch] font-display text-[clamp(2rem,4.4vw,3.25rem)] font-medium leading-[1.04] text-paper">
            The clubhouse keeps a light on.
          </h2>
          {leadIn && (
            <p className="mx-auto mb-8 max-w-[46ch] text-base text-sage">{leadIn}</p>
          )}
          {/* The page's one `flag`, whatever the standing. A member in standing
              is not sold a standing (decision record §12.2): the door leads
              into the clubhouse rather than to the terms. A signed-in viewer
              whose standing is UNKNOWN — still loading, or the read failed —
              may be a member, so the door claims nothing (no lead-in, see
              DOOR_LEAD_IN) and leads to the clubhouse as well: honest for
              anyone signed in, since the gate decides where it actually
              leads. Only someone the club knows holds nothing is invited to
              apply. The CTA budget is unchanged in every state. */}
          {standing === 'none' ? (
            <ClubLinkButton to="/membership">Apply for membership</ClubLinkButton>
          ) : (
            <ClubLinkButton to="/clubhouse">The Clubhouse</ClubLinkButton>
          )}
        </Container>
      </section>
    </ClubPage>
  )
}
